import { useEffect, useState } from 'react'
import { HttpVerb } from '../../../types/api-types'
import { FormDelegate, PageNavigation } from '../../../types/routing-types'
import ConfirmTransition from '../confirm-transition'
import { ERROR_MESSAGES } from '../errors/messages'
import WellEditButtons from './well-edit-buttons'
import WellViewButtons from './well-view-buttons'
import { useGetWellData, useSaveWellData } from '../../hooks/use-api'
import useEditForm, { HandleSave } from '../../hooks/use-edit-form'
import useToastMessage from '../../hooks/use-toast-message'
import useUser from '../../hooks/use-user'
import { isCouncilUserOrAdmin, isDriller } from '../../services/user-utils'
import { useWellState } from '../../context/well-context'
import { useWellId } from '../../hooks/use-well-id'
import { useNavigate } from 'react-router-dom'
import {
  createToastMessage,
  Heading,
  HeadingRow,
  InlineNotification,
  Link,
  RecordContent,
  RecordFooter,
  ToastMessage,
  useTitleChange,
} from '@msaf/core-react'

interface SubmissionErrors {
  [field: string]: Record<string, string> | Array<Record<string, Record<string, string>>>
}

interface SubmissionProps extends FormDelegate {
  isEditMode?: boolean
}

interface SubmissionWrapperProps {
  isEditMode?: boolean
}

export function NewWellSubmission({ setIsDirty, isEditMode }: SubmissionProps) {
  const wellId = useWellId()
  const navigate = useNavigate()
  const { wellState } = useWellState()
  const [displayErrors, setDisplayErrors] = useState<Array<{ id: string; label: string; route: string }>>()
  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${wellId}/errors`,
    method: HttpVerb.POST,
    saveUrl: `wells/${wellId}/submit`,
    publishUrl: `wells/${wellId}/publish`,
    cancelRoute: `/dashboard`,
    saveAndContinueRoute: (id) => `/wells/${wellId}/view/approve`,
  }
  const baseRoute = `/wells/${wellId}/edit`

  useTitleChange('New Well | Submission')
  const { user } = useUser()

  const stateQueryResult = useGetWellData(urls.requestUrl, wellState.isDraft)

  // Handle the two different mutation separately as this is a unique scenario
  // where we have two different mutation endpoints
  const {
    mutate: submit,
    isError: isSubmitError,
    isLoading: isSubmitting,
    error: submitError,
  } = useSaveWellData(urls.saveUrl ?? '', urls.method)

  const {
    mutate: publish,
    isError: isPublishError,
    isLoading: isPublishing,
    error: publishError,
  } = useSaveWellData(urls.publishUrl ?? '', urls.method)

  const {
    isSaved,
    error: fetchError,
    isLoading: isFetching,
    isError: isFetchError,
    fields,
    validationErrors,
  } = useEditForm({ stateQueryResult, setIsDirty })

  const handlePublish: HandleSave = (callback?) => {
    // Passing empty body
    publish(JSON.stringify({}), {
      onSuccess: (data) => {
        createToastMessage(<ToastMessage messageType='success' title='Success' message={'Record has been published'} />)
        callback && callback(data.id)
      },
    })
  }

  const handleSubmit: HandleSave = (callback?) => {
    // Passing empty body
    submit(JSON.stringify({}), {
      onSuccess: (data) => {
        callback && callback(data.id)
      },
    })
  }

  useEffect(() => {
    if (validationErrors && Object.keys(validationErrors).length) {
      // 'Cast' the errors to SubmissionErrors as these errors are structured a bit differently
      // from the regular ValidationErrors. This is because the submission errors need to contain
      // some additional context/data to be able to display the errors messages and also link them to
      // the form.
      const submissionErrors = validationErrors as unknown as SubmissionErrors
      const displayErrors = Object.keys(submissionErrors)?.map((error, index) => {
        const errors = submissionErrors[error]
        if (Array.isArray(errors) && errors.length) {
          // Section error
          const section_errors = errors[0]
          // Just the first one is sufficient to create a new error line to display
          // for these particular set of feilds in the section
          const first_field_error_key = Object.keys(section_errors)[0]
          const field_error_data = section_errors[first_field_error_key]
          const { id, form, objectId, section } = field_error_data
          return {
            id: id,
            label: `${section} are incomplete`,
            route: `${baseRoute}/${form}/${objectId}`,
          }
        } else {
          // Field error
          const { id, form, subform, fieldLabel, objectId } = errors as Record<string, string>
          const label = fieldLabel.endsWith('s') ? `${fieldLabel} are incomplete` : `${fieldLabel} is incomplete`
          return {
            id: id,
            label,
            route: `${baseRoute}/${form}${!subform ? '' : `/${subform}`}${!objectId ? '' : `/${objectId}`}`,
          }
        }
      })
      setDisplayErrors(displayErrors)
    }
  }, [validationErrors, baseRoute])

  useToastMessage({
    errorMessage: ERROR_MESSAGES.SUBMISSION_ERROR_FETCH_FAILED,
    error: fetchError,
    isSuccess: isSaved,
    isError: isFetchError,
  })

  useToastMessage({
    errorMessage: ERROR_MESSAGES.SUBMIT_FOR_APPROVAL_FAILED,
    error: submitError,
    isError: isSubmitError,
  })

  useToastMessage({
    errorMessage: ERROR_MESSAGES.PUBLISH_FAILED,
    error: publishError,
    isError: isPublishError,
  })

  const warningMessage = isCouncilUserOrAdmin(user)
    ? 'There are still incomplete fields:'
    : 'There are still a few things to complete before submitting'

  const isLoading = isFetching || isPublishing || isSubmitting

  return (
    <>
      <RecordContent>
        {!Object.keys(validationErrors)?.length ? (
          <div>
            <HeadingRow isSkeleton={isLoading} headingLevel={2} headingText='Submitting Well Information' />
            <div className='l-content-max-width'>
              <InlineNotification messageType='info' isDismissable={false}>
                Once submitted, you will not be able to change the well information.
              </InlineNotification>
              <br />
              <p>
                The well information provided will be assessed by council. You may be contacted for further information
                or clarification.
              </p>
            </div>
          </div>
        ) : (
          <div>
            <HeadingRow isSkeleton={isLoading} headingLevel={2} headingText='Well information incomplete' />
            <div className='l-content-max-width'>
              <InlineNotification messageType={isCouncilUserOrAdmin(user) ? 'warning' : 'error'} isDismissable={false}>
                <Heading level={6}>{warningMessage}</Heading>
                <ul>
                  {displayErrors?.map(({ id, label, route }) => {
                    return (
                      <li className='c-file-list__item' key={id}>
                        <Link path={route}>{label}</Link>
                      </li>
                    )
                  })}
                </ul>
              </InlineNotification>
              {isDriller(user) && (
                <p>Once all required details have been provided, you will be able to submit the well information.</p>
              )}
              {isCouncilUserOrAdmin(user) && (
                <p>
                  The well information provided will be assessed by council. You may be contacted for further
                  information or clarification.
                </p>
              )}
            </div>
          </div>
        )}

        <RecordFooter>
          {!isEditMode ? (
            <WellViewButtons wellId={wellId} />
          ) : isCouncilUserOrAdmin(user) ? (
            <WellEditButtons
              isLoading={isLoading}
              actions={[
                {
                  label: 'Publish',
                  buttonStyle: 'primary',
                  onClick: () =>
                    handlePublish((id) => {
                      const navigateRoute = urls.saveAndContinueRoute(id)
                      navigateRoute && navigate(navigateRoute, { state: { isProgrammaticTransition: true } })
                    }),
                  isDisabled: !!fields.isPublished,
                },
                {
                  label: 'Submit for approval',
                  buttonStyle: 'primary',
                  onClick: () =>
                    handleSubmit((id) => {
                      const navigateRoute = urls.saveAndContinueRoute(id)
                      navigateRoute &&
                        navigate(navigateRoute, { state: { isProgrammaticTransition: true, submitted: true } })
                    }),
                  isDisabled: !!fields.isUnderReview,
                },
              ]}
            />
          ) : (
            <WellEditButtons
              isLoading={isLoading}
              actions={[
                {
                  label: 'Submit for approval',
                  buttonStyle: 'primary',
                  onClick: () =>
                    handleSubmit((id) => {
                      const navigateRoute = urls.saveAndContinueRoute(id)
                      navigateRoute &&
                        navigate(navigateRoute, { state: { isProgrammaticTransition: true, submitted: true } })
                    }),
                  isDisabled: !!Object.keys(validationErrors).length,
                },
              ]}
            />
          )}
        </RecordFooter>
      </RecordContent>
    </>
  )
}

export default function NewWellSubmissionWrapped(props: SubmissionWrapperProps) {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <NewWellSubmission setIsDirty={setIsDirty} {...props} />
    </ConfirmTransition>
  )
}
